import { useState } from "react";
import type { ReactNode } from "react";
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, TextField,
} from "@mui/material";

/**
 * Confirming something that cannot be undone.
 *
 * `typeToConfirm` is for the ones that really cannot: destroying a tenant or
 * deleting its volumes should cost more than a reflex click on the same button
 * position as "Cancel".
 */
export default function Confirm({
  open, title, body, confirmLabel = "Confirm", danger = false, busy = false,
  error, typeToConfirm, confirmDisabled = false, onCancel, onConfirm,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  error?: unknown;
  typeToConfirm?: string;
  /** Holds the confirm button back until the form in `body` is complete. */
  confirmDisabled?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState("");
  const blocked = confirmDisabled || (!!typeToConfirm && typed.trim() !== typeToConfirm);

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {typeof body === "string" ? <DialogContentText>{body}</DialogContentText> : body}
        {typeToConfirm ? (
          <TextField
            fullWidth size="small" sx={{ mt: 2 }}
            label={`Type ${typeToConfirm} to confirm`}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            autoComplete="off"
          />
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error instanceof Error ? error.message : String(error)}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={danger ? "error" : "primary"}
          disabled={busy || blocked}
        >
          {busy ? "Working…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
